import { createContext, PropsWithChildren, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SetValue } from 'hooks/useCookies';
import useCryptoErrorToast from 'hooks/useCryptoErrorToast';
import useLocalStorage from 'hooks/useLocalStorage/useLocalStorage';
import useSessionLoggedOut from 'hooks/useSessionLoggedOut';
import useMxCookies from 'modules/Authentication/hooks/useMxCookies';
import { useIsRoomRoute } from 'routes/hooks/useIsRoomRoute';
import constants from 'utils/Constants';
import { SetState } from 'utils/types';
import { isGuestModeEnabled } from 'utils/utils';

export type AuthProviderContextType = {
  isContinueAsGuest: boolean;
  setContinueAsGuest: SetState<boolean>;
  authPageVisited: boolean;
  setAuthPageVisited: SetValue<boolean>;
};

export const AuthProviderContext = createContext<AuthProviderContextType | null>(null);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const { mxUserId, mxDeviceId, mxAccessToken } = useMxCookies();
  const [isContinueAsGuest, setContinueAsGuest] = useState(false);
  const [authPageVisited, setAuthPageVisited] = useLocalStorage(constants.AUTH_PAGE_VISITED, false, true);
  const [searchParams] = useSearchParams();
  const isRoomRoute = useIsRoomRoute();

  useSessionLoggedOut();
  useCryptoErrorToast();

  useEffect(() => {
    const loginToken = searchParams.get('loginToken');
    const isUnauthenticated = !mxUserId || !mxAccessToken || !mxDeviceId;

    const shouldEnableGuestMode =
      isGuestModeEnabled() && isRoomRoute && isUnauthenticated && !isContinueAsGuest && !loginToken;

    if (shouldEnableGuestMode) setContinueAsGuest(true);
  }, [isRoomRoute, mxUserId, mxAccessToken, mxDeviceId, isContinueAsGuest, searchParams]);

  const state: AuthProviderContextType = {
    isContinueAsGuest,
    setContinueAsGuest,
    authPageVisited,
    setAuthPageVisited,
  };

  return <AuthProviderContext.Provider value={state}>{children}</AuthProviderContext.Provider>;
};
